import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, BookOpen, Pencil, FolderPlus, Trash2, Settings, ChevronRight, ArrowLeft, Plus, RefreshCw } from 'lucide-react';
import '../../css/book_details_modal.css';

const BookDetailsModal = ({
  selectedBook,
  handleEditClick,
  deleteBook,
  format,
  handleFormatClick,
  special,
  handleSpecialClick,
  convertTimestampToDate,
  collections,
  selectedCollectionId,
  handleCollectionSelect,
  handleAddToCollection,
  addToCollectionMessage,
  bookCollections,
  READ_STATE,
  OWNERSHIP_STATE,
  handleReadStateChange,
  handleOwnershipStateChange,
  handleCloseBookDetails,
  handleRefreshMetadata,
  isRefreshingMetadata,
}) => {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [modalView, setModalView] = useState('details'); // 'details' | 'advanced'
  const dragStartY = useRef(0);
  const modalRef = useRef(null);
  const wasOpen = useRef(false);

  const CLOSE_THRESHOLD = 120;

  useEffect(() => {
    if (selectedBook && !wasOpen.current) {
      setIsOpening(true);
      setModalView('details');
      const frame = requestAnimationFrame(() => setIsOpening(false));
      wasOpen.current = true;
      return () => cancelAnimationFrame(frame);
    }
    if (!selectedBook) {
      setModalView('details');
    }
    wasOpen.current = !!selectedBook;
  }, [selectedBook]);

  const handleTouchStart = useCallback((e) => {
    const modal = modalRef.current;
    if (modal && modal.scrollTop > 0) return;
    dragStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const delta = Math.max(0, currentY - dragStartY.current);
    setDragY(delta);
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > CLOSE_THRESHOLD) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setDragY(0);
        handleCloseBookDetails();
      }, 300);
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY, handleCloseBookDetails]);

  const modalStyle = isClosing
    ? { transform: 'translateY(100%)', transition: 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)' }
    : isDragging
      ? { transform: `translateY(${dragY}px)`, transition: 'none' }
      : isOpening
        ? { transform: 'translateY(100%)' }
        : { transform: 'translateY(0)', transition: 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)' };

  const getReadStatusLabel = (value) => {
    if (value === READ_STATE[0]) return 'READ';
    if (value === READ_STATE[1]) return 'UNREAD';
    if (value === READ_STATE[2]) return 'READING';
    return 'READING';
  };

  const getReadStatusClass = (value) => {
    if (value === READ_STATE[0]) return 'chip-read';
    if (value === READ_STATE[1]) return 'chip-unread';
    if (value === READ_STATE[2]) return 'chip-reading';
    return 'chip-reading';
  };

  const getOwnershipLabel = (value) => {
    if (value === OWNERSHIP_STATE[0]) return 'OWNED';
    if (value === OWNERSHIP_STATE[1]) return 'ORDERED';
    if (value === OWNERSHIP_STATE[2]) return 'WANTED';
    return 'OWNED';
  };

  const getModalStatusStyle = (value) => {
    switch (value) {
      case READ_STATE[0]:
        return { backgroundColor: 'var(--color-status-read-bg)', color: 'var(--color-status-read)', borderColor: 'var(--color-border)' };
      case READ_STATE[1]:
        return { backgroundColor: 'var(--color-status-unread-bg)', color: 'var(--color-status-unread)', borderColor: 'var(--color-border)' };
      case READ_STATE[2]:
        return { backgroundColor: 'var(--color-status-reading-bg)', color: 'var(--color-status-reading)', borderColor: 'var(--color-border)' };
      case OWNERSHIP_STATE[0]:
        return { backgroundColor: 'var(--color-status-owned-bg)', color: 'var(--color-status-owned)', borderColor: 'var(--color-border)' };
      case OWNERSHIP_STATE[1]:
        return { backgroundColor: 'var(--color-status-ordered-bg)', color: 'var(--color-status-ordered)', borderColor: 'var(--color-border)' };
      case OWNERSHIP_STATE[2]:
        return { backgroundColor: 'var(--color-status-wanted-bg)', color: 'var(--color-status-wanted)', borderColor: 'var(--color-border)' };
      default:
        return { backgroundColor: 'var(--color-status-reading-bg)', color: 'var(--color-status-reading)', borderColor: 'var(--color-border)' };
    }
  };

  if (!selectedBook) return null;

  const coverImage = (selectedBook.use_custom_image && selectedBook.uploaded_image_b64 !== "")
    ? selectedBook.uploaded_image_b64
    : selectedBook.imageLinks !== "{}"
      ? JSON.parse(selectedBook.imageLinks).thumbnail
      : null;

  const isOwner = selectedBook.owner === localStorage.getItem('loggedInUsername');

  return (
      <div className="book-details-modal" onClick={handleCloseBookDetails}>
        <article
          className="modal-content"
          ref={modalRef}
          style={modalStyle}
          onClick={(event) => event.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Mobile drag handle */}
          <div className="modal-drag-handle" aria-hidden="true">
            <span className="modal-drag-bar"></span>
          </div>

          {/* Desktop two-panel layout */}
          <div className="modal-panels">
            {/* Left: Cover Panel (shared across views) */}
            <div className="modal-cover-panel">
              <div className="modal-cover-image">
                {coverImage ? (
                  <img src={coverImage} alt={selectedBook.title} />
                ) : (
                  <BookOpen size={48} className="modal-cover-placeholder-icon" />
                )}
              </div>
              <div className="modal-cover-status-row">
                <select
                  className={`modal-status-chip ${getReadStatusClass(selectedBook.read)}`}
                  value={selectedBook.read}
                  onChange={handleReadStateChange}
                >
                  <option value={READ_STATE[2]}>READING</option>
                  <option value={READ_STATE[1]}>UNREAD</option>
                  <option value={READ_STATE[0]}>READ</option>
                </select>
                <select
                  className="modal-status-chip chip-outlined"
                  value={selectedBook.ownership}
                  onChange={handleOwnershipStateChange}
                >
                  <option value={OWNERSHIP_STATE[0]}>OWNED</option>
                  <option value={OWNERSHIP_STATE[1]}>ORDERED</option>
                  <option value={OWNERSHIP_STATE[2]}>WANTED</option>
                </select>
              </div>
            </div>

            {/* Right: Detail Panel — view-switched */}
            {modalView === 'details' ? (
              <div className="modal-detail-panel">
                <div className="modal-close-row">
                  <button className="modal-close-btn" onClick={handleCloseBookDetails} aria-label="Close">
                    <X size={20} />
                  </button>
                </div>

                <div className="modal-title-section">
                  <h2 className="modal-book-title">{selectedBook.title}</h2>
                  <span className="modal-book-author">by {selectedBook.author}</span>
                </div>

                <div className="modal-metadata">
                  <div className="modal-meta-col">
                    <div className="modal-meta-row">
                      <span className="modal-meta-label">PUBLISHER</span>
                      <span className="modal-meta-value">{selectedBook.publisher}</span>
                    </div>
                    <div className="modal-meta-row">
                      <span className="modal-meta-label">ISBN</span>
                      <span className="modal-meta-value">{selectedBook.isbn}</span>
                    </div>
                  </div>
                  <div className="modal-meta-col">
                    <div className="modal-meta-row">
                      <span className="modal-meta-label">PUBLISHED</span>
                      <span className="modal-meta-value">{selectedBook.publishedDate}</span>
                    </div>
                    <div className="modal-meta-row">
                      <span className="modal-meta-label">FORMAT</span>
                      <button className="modal-meta-value modal-meta-btn" onClick={() => handleFormatClick(selectedBook)}>
                        {selectedBook.format ? 'Digital' : 'Paperback'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="modal-divider" />

                <div className="modal-desc-section">
                  <span className="modal-meta-label">DESCRIPTION</span>
                  <p className="modal-desc-text">{selectedBook.description}</p>
                </div>

                <button
                  className="modal-advanced-toggle"
                  onClick={() => setModalView('advanced')}
                >
                  <Settings size={14} />
                  <span>Advanced Options</span>
                  <ChevronRight size={14} />
                </button>

                <div className="modal-actions">
                  {isOwner && (
                    <>
                      <button className="modal-action-btn modal-action-edit" onClick={handleEditClick}>
                        <Pencil size={14} />
                        <span>Edit Book</span>
                      </button>
                      {collections.length > 0 && (
                        <button className="modal-action-btn modal-action-collection" onClick={handleAddToCollection}>
                          <FolderPlus size={14} />
                          <span>Add to Collection</span>
                        </button>
                      )}
                      <div className="modal-action-spacer" />
                      <button className="modal-action-btn modal-action-delete" onClick={() => deleteBook(selectedBook.id)}>
                        <Trash2 size={14} />
                        <span>Delete</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* Advanced Options View */
              <div className="modal-detail-panel">
                <div className="modal-close-row modal-close-row-advanced">
                  <button className="modal-back-btn" onClick={() => setModalView('details')}>
                    <ArrowLeft size={16} />
                    <span>Back to Details</span>
                  </button>
                  <button className="modal-close-btn" onClick={handleCloseBookDetails} aria-label="Close">
                    <X size={20} />
                  </button>
                </div>

                <div className="modal-title-section">
                  <h2 className="modal-book-title">Advanced Options</h2>
                  <span className="modal-book-author">{selectedBook.title} by {selectedBook.author}</span>
                </div>

                <div className="modal-divider" />

                <div className="modal-metadata">
                  <div className="modal-meta-col">
                    <div className="modal-meta-row">
                      <span className="modal-meta-label">EDITION</span>
                      <button className="modal-meta-value modal-meta-btn" onClick={() => handleSpecialClick(selectedBook)}>
                        {selectedBook.special ? 'Special' : 'Standard'}
                      </button>
                    </div>
                    <div className="modal-meta-row">
                      <span className="modal-meta-label">ADDED</span>
                      <span className="modal-meta-value">{convertTimestampToDate(selectedBook.timestamp)}</span>
                    </div>
                  </div>
                  <div className="modal-meta-col">
                    <div className="modal-meta-row">
                      <span className="modal-meta-label">ADDED BY</span>
                      <span className="modal-meta-value">{selectedBook.owner}</span>
                    </div>
                    <div className="modal-meta-row">
                      <span className="modal-meta-label">COLLECTIONS</span>
                      <span className="modal-meta-value">{bookCollections.length > 0 ? bookCollections.join(', ') : 'None'}</span>
                    </div>
                  </div>
                </div>

                <div className="modal-divider" />

                {collections.length > 0 && (
                  <div className="modal-advanced-add-collection">
                    <select value={selectedCollectionId} onChange={handleCollectionSelect}>
                      {collections.map((collection) => (
                        <option key={collection.id} value={collection.id}>{collection.name}</option>
                      ))}
                    </select>
                    <button className="modal-advanced-add-btn" onClick={handleAddToCollection}>
                      <Plus size={12} />
                      <span>Add</span>
                    </button>
                  </div>
                )}

                {addToCollectionMessage && addToCollectionMessage.length > 0 && (
                  <p className="modal-collection-msg">{addToCollectionMessage}</p>
                )}

                {isOwner && (
                  <button className="modal-advanced-refresh-btn" onClick={handleRefreshMetadata} disabled={isRefreshingMetadata}>
                    <RefreshCw size={14} />
                    <span>{isRefreshingMetadata ? 'Refreshing...' : 'Refresh Metadata'}</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Mobile-only fallback sections (hidden on desktop) */}
          <div className="modal-mobile-only">
            <h2 className="model-h2">
              <span className="modal-title">{selectedBook.title}</span>
            </h2>
            <div className="book-description-container">
              {coverImage ? (
                <img className="book-thumbnail" src={coverImage} alt={selectedBook.title} />
              ) : null}
              <div className="book-description">
                <p>{selectedBook.description}</p>
              </div>
            </div>
            <div className="book-details-grid">
              <div className="book-detail-item">
                <span className="detail-label">Edition:</span>
                <button className='search-button' onClick={() => handleSpecialClick(selectedBook)}>
                  {selectedBook.special ? 'Special' : 'Standard'}
                </button>
              </div>
              <div className="book-detail-item">
                <span className="detail-label">Format:</span>
                <button className='search-button' onClick={() => handleFormatClick(selectedBook)}>
                  {selectedBook.format ? 'Digital' : 'Physical'}
                </button>
              </div>
              <div className="book-detail-item">
                <span className="detail-label">ISBN:</span>
                <span className="detail-value">{selectedBook.isbn}</span>
              </div>
              <div className="book-detail-item">
                <span className="detail-label">Publisher:</span>
                <span className="detail-value">{selectedBook.publisher}</span>
              </div>
              <div className="book-detail-item">
                <span className="detail-label">Published Date:</span>
                <span className="detail-value">{selectedBook.publishedDate}</span>
              </div>
              <div className="book-detail-item">
                <span className="detail-label">Author:</span>
                <span className="detail-value">{selectedBook.author}</span>
              </div>
              <div className="book-detail-item">
                <span className="detail-label">Added:</span>
                <span className="detail-value">{convertTimestampToDate(selectedBook.timestamp)}</span>
              </div>
              <div className="book-detail-item">
                <span className="detail-label">Added By:</span>
                <span className="detail-value">{selectedBook.owner}</span>
              </div>
              {bookCollections.length > 0 && (
                <div className="book-detail-item detail-wide">
                  <span className="detail-label">Collections:</span>
                  <select className="select-box-modal detail-value">
                    {bookCollections.map((collection) => (
                      <option key={collection}>{collection}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {collections.length > 0 && (
              <>
                <div className="modal-inline-actions">
                  <select className="select-box-modal" value={selectedCollectionId} onChange={handleCollectionSelect}>
                    {collections.map((collection) => (
                      <option key={collection.id} value={collection.id}>{collection.name}</option>
                    ))}
                  </select>
                  <button className="search-button" onClick={handleAddToCollection}>Add to Collection</button>
                </div>
                {addToCollectionMessage && addToCollectionMessage.length > 0 && (
                  <p>{addToCollectionMessage}</p>
                )}
              </>
            )}

            <div className="status-select-row">
              <select
                className='select-box-modal status-select'
                value={selectedBook.read}
                onChange={handleReadStateChange}
                style={getModalStatusStyle(selectedBook.read)}
              >
                <option value={READ_STATE[2]}>READING</option>
                <option value={READ_STATE[1]}>UNREAD</option>
                <option value={READ_STATE[0]}>READ</option>
              </select>
              <select
                className="select-box-modal status-select"
                value={selectedBook.ownership}
                onChange={handleOwnershipStateChange}
                style={getModalStatusStyle(selectedBook.ownership)}
              >
                <option value={OWNERSHIP_STATE[0]}>OWNED</option>
                <option value={OWNERSHIP_STATE[1]}>ORDERED</option>
                <option value={OWNERSHIP_STATE[2]}>WANTED</option>
              </select>
            </div>

            <div className="edit-modal-buttons modal-action-row">
              {isOwner && (
                <>
                  <button className="btn" onClick={handleRefreshMetadata} disabled={isRefreshingMetadata}>
                    {isRefreshingMetadata ? 'Refreshing...' : 'Refresh Metadata'}
                  </button>
                  <button className="btn" onClick={handleEditClick}>
                    Edit Details
                  </button>
                  <button className='btn btn-danger' onClick={() => deleteBook(selectedBook.id)}>
                    Delete Book
                  </button>
                </>
              )}
            </div>
          </div>
        </article>
      </div>
  );
};

export default BookDetailsModal;
